import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Attach JWT from localStorage to every request.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("iva_access_token");
  if (token) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }
  return config;
});

// 401 / 403 from auth-protected routes → clear token + bounce to login.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const status = err?.response?.status;
    const isAuthRoute = (err?.config?.url || "").includes("/auth/");
    if (status === 401 && !isAuthRoute) {
      localStorage.removeItem("iva_access_token");
      localStorage.removeItem("iva_current_user_id");
      if (window.location.pathname !== "/login") {
        window.location.assign("/login");
      }
    }
    return Promise.reject(err);
  },
);

// Auth
export const authLogin = (mobile, password) =>
  api.post("/auth/login", { mobile, password }).then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authChangePassword = (current_password, new_password) =>
  api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data);
export const authAdminResetPassword = (user_id, new_password) =>
  api.post("/auth/admin-reset-password", { user_id, new_password }).then((r) => r.data);

// Module Access Matrix
export const listModulePermissions = () =>
  api.get("/admin/module-permissions").then((r) => r.data);
export const updateModulePermissions = (role, permissions) =>
  api.put(`/admin/module-permissions/${role}`, { permissions }).then((r) => r.data);


// Locations
export const listLocations = () => api.get("/locations").then((r) => r.data);
export const createLocation = (b) => api.post("/locations", b).then((r) => r.data);
export const updateLocation = (id, b) => api.put(`/locations/${id}`, b).then((r) => r.data);
export const deleteLocation = (id) => api.delete(`/locations/${id}`).then((r) => r.data);

// Users
export const listUsers = () => api.get("/users").then((r) => r.data);
export const getUser = (id) => api.get(`/users/${id}`).then((r) => r.data);
export const createUser = (b, params = {}) =>
  api.post("/users", b, { params }).then((r) => r.data);
export const updateUser = (id, b, params = {}) =>
  api.put(`/users/${id}`, b, { params }).then((r) => r.data);
export const deleteUser = (id, params = {}) =>
  api.delete(`/users/${id}`, { params }).then((r) => r.data);
export const setUserStatus = (id, b) =>
  api.post(`/users/${id}/set-status`, b).then((r) => r.data);

// Parts Master
export const listParts = (q = "", limit = 5000) =>
  api.get("/parts-master", { params: { q, limit } }).then((r) => r.data);

export const lookupPartByNumber = (partNumber) =>
  api.get(`/parts-master/lookup/${encodeURIComponent(partNumber)}`).then((r) => r.data);
export const createPart = (b) => api.post("/parts-master", b).then((r) => r.data);
export const updatePart = (id, b) => api.put(`/parts-master/${id}`, b).then((r) => r.data);
export const deletePart = (id) => api.delete(`/parts-master/${id}`).then((r) => r.data);
export const partsMeta = () => api.get("/parts-master/meta").then((r) => r.data);
export const partsSampleTemplate = () =>
  api.get("/parts-master/sample-template", { responseType: "blob" }).then((r) => r);
export const partsUploadPreview = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/parts-master/upload-preview", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};
export const partsUploadConfirm = (body) =>
  api.post("/parts-master/upload-confirm", body).then((r) => r.data);

// WhatsApp Templates
export const listTemplates = () => api.get("/whatsapp-templates").then((r) => r.data);
export const createTemplate = (b) => api.post("/whatsapp-templates", b).then((r) => r.data);
export const updateTemplate = (id, b) => api.put(`/whatsapp-templates/${id}`, b).then((r) => r.data);
export const deleteTemplate = (id) => api.delete(`/whatsapp-templates/${id}`).then((r) => r.data);

// Orders
export const listOrders = (params = {}) => api.get("/orders", { params }).then((r) => r.data);
export const getOrder = (id) => api.get(`/orders/${id}`).then((r) => r.data);
export const createOrder = (b) => api.post("/orders", b).then((r) => r.data);
export const updateOrder = (id, b) => api.put(`/orders/${id}`, b).then((r) => r.data);
export const markOrdered = (id, b) => api.post(`/orders/${id}/mark-ordered`, b).then((r) => r.data);
export const markInvoiced = (id, b) => api.post(`/orders/${id}/invoiced`, b).then((r) => r.data);
export const receiveParts = (id, b) => api.post(`/orders/${id}/receive-parts`, b).then((r) => r.data);
// markInvoiced declared above
export const updateEdd = (id, b) => api.post(`/orders/${id}/update-edd`, b).then((r) => r.data);
export const sendReminder = (id, b) => api.post(`/orders/${id}/send-reminder`, b).then((r) => r.data);
export const cnrOrder = (id, b) =>
  api.post(`/orders/${id}/customer-not-responding`, b).then((r) => r.data);
export const returnToStock = (id, b) => api.post(`/orders/${id}/return-to-stock`, b).then((r) => r.data);
export const cancelOrder = (id, b) => api.post(`/orders/${id}/cancel`, b).then((r) => r.data);
export const deleteOrder = (id, actor, actor_role) =>
  api.delete(`/orders/${id}`, { params: { actor, actor_role } }).then((r) => r.data);
export const ordersByMobile = (m) => api.get(`/orders/by-mobile/${m}`).then((r) => r.data);
export const previewTemplate = (order_ids) =>
  api.post("/orders/preview-template", { order_ids }).then((r) => r.data);
export const generateTemplate = (order_ids) =>
  api.post("/orders/generate-template", { order_ids }, { responseType: "blob" }).then((r) => r);
export const markOrderedBulk = (b) => api.post("/orders/mark-ordered-bulk", b).then((r) => r.data);
export const listDeletedOrders = () => api.get("/deleted-orders").then((r) => r.data);
export const previewOrderExcel = (id) => api.post(`/orders/${id}/preview-excel`).then((r) => r.data);
export const generateOrderExcel = (id) =>
  api.post(`/orders/${id}/generate-excel`, {}, { responseType: "blob" }).then((r) => r);

// Dashboard / Reports
export const dashboardStats = (location_code) =>
  api.get("/dashboard/stats", { params: { location_code } }).then((r) => r.data);
export const dashboardQuickLists = (location_code) =>
  api.get("/dashboard/quick-lists", { params: { location_code } }).then((r) => r.data);
export const dashboardByLocation = () =>
  api.get("/dashboard/by-location").then((r) => r.data);
export const reportsSummary = (params = {}) =>
  api.get("/reports/summary", { params }).then((r) => r.data);

export const workbenchCounts = (location_code) =>
  api.get("/workbench/counts", { params: { location_code } }).then((r) => r.data);
export const seed = () => api.post("/seed").then((r) => r.data);

export const submitDraft = (id, body) =>
  api.post(`/orders/${id}/submit-draft`, body).then((r) => r.data);

export const exportOrders = (params = {}) =>
  api.get("/orders/export", { params, responseType: "blob" }).then((r) => r);

// Activity Log (audit trail)
export const listActivityLogs = (params = {}) =>
  api.get("/activity-logs", { params }).then((r) => r.data);


// Admin · Danger Zone
export const adminResetOrderData = (confirm) =>
  api.post("/admin/reset-order-data", { confirm }).then((r) => r.data);


// Order attachments (Upload Estimate)
export const uploadOrderAttachment = (orderId, file, actor, actor_role, label = "estimate") => {
  const fd = new FormData();
  fd.append("file", file);
  if (actor) fd.append("actor", actor);
  if (actor_role) fd.append("actor_role", actor_role);
  fd.append("label", label);
  return api
    .post(`/orders/${orderId}/attachments`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};

export const deleteOrderAttachment = (orderId, attachmentId, actor, actor_role) =>
  api
    .delete(`/orders/${orderId}/attachments/${attachmentId}`, {
      params: { actor, actor_role },
    })
    .then((r) => r.data);

export const downloadOrderAttachmentUrl = (orderId, attachmentId) =>
  `${API}/orders/${orderId}/attachments/${attachmentId}/download`;

// Order advance payment
export const setOrderAdvance = (orderId, body) =>
  api.post(`/orders/${orderId}/advance`, body).then((r) => r.data);

export const deleteOrderAdvance = (orderId, actor, actor_role) =>
  api.delete(`/orders/${orderId}/advance`, { params: { actor, actor_role } }).then((r) => r.data);



// Packing Slips
export const createPackingSlip = (body) =>
  api.post("/packing-slips", body).then((r) => r.data);

export const listPackingSlips = (params = {}) =>
  api.get("/packing-slips", { params }).then((r) => r.data);

export const getPackingSlip = (id) =>
  api.get(`/packing-slips/${id}`).then((r) => r.data);

export const updatePackingSlip = (id, body) =>
  api.put(`/packing-slips/${id}`, body).then((r) => r.data);

export const dispatchPackingSlip = (id, actor, actor_role) =>
  api.post(`/packing-slips/${id}/dispatch`, null, { params: { actor, actor_role } }).then((r) => r.data);

export const deletePackingSlip = (id, actor, actor_role) =>
  api.delete(`/packing-slips/${id}`, { params: { actor, actor_role } }).then((r) => r.data);

// Stock Planning
export const spUploadConsumption = (formData) =>
  api.post("/stock-planning/consumption/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);

export const spListConsumption = (params = {}) =>
  api.get("/stock-planning/consumption", { params }).then((r) => r.data);

export const spDeleteConsumption = (id, actor, actor_role) =>
  api.delete(`/stock-planning/consumption/${id}`, { params: { actor, actor_role } }).then((r) => r.data);

export const spCalculateABCD = (location_code, actor, actor_role) => {
  const fd = new FormData();
  fd.append("location_code", location_code);
  if (actor) fd.append("actor", actor);
  if (actor_role) fd.append("actor_role", actor_role);
  return api.post("/stock-planning/abcd/calculate", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);
};

export const spGetABCD = (params = {}) =>
  api.get("/stock-planning/abcd", { params }).then((r) => r.data);

export const spGenerateSuggested = (formData) =>
  api.post("/stock-planning/suggested-orders/generate", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);

export const spGetSuggested = (params = {}) =>
  api.get("/stock-planning/suggested-orders", { params }).then((r) => r.data);

export const spVerifyOrder = (formData) =>
  api.post("/stock-planning/verification/verify", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }).then((r) => r.data);

export const spGetVerification = (params = {}) =>
  api.get("/stock-planning/verification", { params }).then((r) => r.data);

export const spDashboard = (params = {}) =>
  api.get("/stock-planning/dashboard", { params }).then((r) => r.data);

export const spActivity = (params = {}) =>
  api.get("/stock-planning/activity", { params }).then((r) => r.data);

export const spActivityUsers = (params = {}) =>
  api.get("/stock-planning/activity-users", { params }).then((r) => r.data);

// ===================== Accidental Vehicles (AV) =====================
export const avCreate = (body) => api.post("/av", body).then((r) => r.data);
export const avList = (params = {}) => api.get("/av", { params }).then((r) => r.data);
export const avGet = (id) => api.get(`/av/${id}`).then((r) => r.data);
export const avUpdate = (id, body) => api.put(`/av/${id}`, body).then((r) => r.data);
export const avHold = (id, body) => api.post(`/av/${id}/hold`, body).then((r) => r.data);
export const avResume = (id, body) => api.post(`/av/${id}/resume`, body).then((r) => r.data);
export const avDeliver = (id, body) => api.post(`/av/${id}/deliver`, body).then((r) => r.data);
export const avDashboard = (params = {}) =>
  api.get("/av/dashboard", { params }).then((r) => r.data);
export const avReport = (params = {}) =>
  api.get("/av/report", { params }).then((r) => r.data);
export const avReportExport = (params = {}) =>
  api.get("/av/report/export", { params, responseType: "blob" }).then((r) => r);
export const avUploadAttachment = (id, fd) =>
  api.post(`/av/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
export const avAttachmentUrl = (id, attId) => `${API}/av/${id}/attachments/${attId}`;
export const avDeleteAttachment = (id, attId, params = {}) =>
  api.delete(`/av/${id}/attachments/${attId}`, { params }).then((r) => r.data);
export const avDelete = (id) => api.delete(`/av/${id}`).then((r) => r.data);


export const avAddEstimate = (id, fd) =>
  api.post(`/av/${id}/estimates`, fd, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
export const avManualApproveEstimate = (id, estId, body) =>
  api.post(`/av/${id}/estimates/${estId}/manual-approve`, body).then((r) => r.data);
export const avUpdateParts = (id, body) =>
  api.put(`/av/${id}/parts`, body).then((r) => r.data);
export const avAddPartItem = (id, body) =>
  api.post(`/av/${id}/parts/items`, body).then((r) => r.data);
export const avUpdatePartItem = (id, itemId, body) =>
  api.put(`/av/${id}/parts/items/${itemId}`, body).then((r) => r.data);
export const avDeletePartItem = (id, itemId, params = {}) =>
  api.delete(`/av/${id}/parts/items/${itemId}`, { params }).then((r) => r.data);
export const avUpdateRepair = (id, body) =>
  api.put(`/av/${id}/repair`, body).then((r) => r.data);
export const avTimeline = (id, params = {}) =>
  api.get(`/av/${id}/timeline`, { params }).then((r) => r.data);

export const avAddAdvance = (id, body) =>
  api.post(`/av/${id}/advances`, body).then((r) => r.data);
export const avDeleteAdvance = (id, advId, params = {}) =>
  api.delete(`/av/${id}/advances/${advId}`, { params }).then((r) => r.data);
export const avUpdateBilling = (id, body) =>
  api.put(`/av/${id}/billing`, body).then((r) => r.data);

export const avAddCrmNote = (id, body) =>
  api.post(`/av/${id}/crm-notes`, body).then((r) => r.data);
export const avDeleteCrmNote = (id, noteId, params = {}) =>
  api.delete(`/av/${id}/crm-notes/${noteId}`, { params }).then((r) => r.data);

export const spExportSuggested = (params = {}) =>
  api.get("/stock-planning/suggested-orders/export", { params, responseType: "blob" }).then((r) => r);

export const spExportVerification = (params = {}) =>
  api.get("/stock-planning/verification/export", { params, responseType: "blob" }).then((r) => r);

export const pvStatuses = (includeInactive = true) =>
  api.get("/pv/statuses", { params: { include_inactive: includeInactive } }).then((r) => r.data);
export const pvStatusCreate = (body) =>
  api.post("/pv/statuses", body).then((r) => r.data);
export const pvStatusUpdate = (id, body) =>
  api.put(`/pv/statuses/${id}`, body).then((r) => r.data);
export const pvStatusSetActive = (id, is_active) =>
  api.post(`/pv/statuses/${id}/set-active`, { is_active }).then((r) => r.data);

// PV Workbench (Stage 4)
export const pvWorkbench = (params) =>
  api.get("/pv/workbench", { params }).then((r) => r.data);
export const pvInlineUpdate = (id, body) =>
  api.put(`/pv/${id}/update`, body).then((r) => r.data);
export const pvHistory = (id) =>
  api.get(`/pv/${id}/history`).then((r) => r.data);
export const pvUpdateParts = (id, body) =>
  api.put(`/pv/${id}/parts`, body).then((r) => r.data);
export const pvAddFollowup = (id, body) =>
  api.post(`/pv/${id}/followup`, body).then((r) => r.data);
export const pvRejects = (params) =>
  api.get("/pv/rejects", { params }).then((r) => r.data);

// PV Stage 5 — delivery flow
export const pvMarkDelivered = (id, body) =>
  api.post(`/pv/${id}/mark-delivered`, body).then((r) => r.data);
export const pvReturnToActive = (id, body) =>
  api.post(`/pv/${id}/return-to-active-pending`, body).then((r) => r.data);
export const pvReopenDelivered = (id, body) =>
  api.post(`/pv/${id}/reopen-delivered`, body).then((r) => r.data);

// PV Stage 7 — Dashboard & Reports
export const pvDashboardSummary = (params) =>
  api.get("/pv/dashboard/summary", { params }).then((r) => r.data);
export const pvDashboardLocation = (code, params) =>
  api.get(`/pv/dashboard/location/${code}`, { params }).then((r) => r.data);
export const pvReportAgeing = (params) =>
  api.get("/pv/reports/ageing", { params }).then((r) => r.data);
export const pvReportDailySummary = (params) =>
  api.get("/pv/reports/daily-summary", { params }).then((r) => r.data);
export const pvReportEddCompliance = (params) =>
  api.get("/pv/reports/edd-compliance", { params }).then((r) => r.data);
export const pvReportStatusWise = (params) =>
  api.get("/pv/reports/status-wise", { params }).then((r) => r.data);
export const pvReportServicePerformance = (params) =>
  api.get("/pv/reports/service-performance", { params }).then((r) => r.data);

// PV Upload history
export const pvImports = (params) =>
  api.get("/pv/imports", { params }).then((r) => r.data);

// Security Module — YBC Dispatch Verification
export const securityLookup = (slip_number) =>
  api.get("/security/dispatch/lookup", { params: { slip_number } }).then((r) => r.data);
export const securityAwaiting = () =>
  api.get("/security/dispatch/awaiting").then((r) => r.data);
export const securityHistory = (params) =>
  api.get("/security/dispatch/history", { params }).then((r) => r.data);
export const securityApprove = (slip_id, body) =>
  api.post(`/security/dispatch/${slip_id}/approve`, body).then((r) => r.data);
export const securityHold = (slip_id, body) =>
  api.post(`/security/dispatch/${slip_id}/hold`, body).then((r) => r.data);

// Legacy URL builders — kept for backwards compat but should NOT be used as
// <a href>. Anonymous browser navigation bypasses the Authorization header
// and will 401. Use spExportSuggested / spExportVerification (blob download).
export const spExportSuggestedUrl = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return `${API}/stock-planning/suggested-orders/export${qs ? `?${qs}` : ""}`;
};

export const spExportVerificationUrl = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return `${API}/stock-planning/verification/export${qs ? `?${qs}` : ""}`;
};

// LOC Tracker (Vehicle Retention — Phase 1)
export const locTatSettings = () => api.get("/loc/tat-settings").then((r) => r.data);
export const locUpdateTatSettings = (body) => api.put("/loc/tat-settings", body).then((r) => r.data);
export const locTrigger = (body) => api.post("/loc/trigger", body).then((r) => r.data);
export const locList = (params = {}) => api.get("/loc", { params }).then((r) => r.data);
export const locGet = (id) => api.get(`/loc/${id}`).then((r) => r.data);
export const locLetterBlob = (id, stage) =>
  api.get(`/loc/${id}/letter/${stage}`, { responseType: "blob" }).then((r) => r.data);
export const locGenerate = (id, stage, body) => {
  const fd = new FormData();
  Object.entries(body || {}).forEach(([k, v]) => { if (v != null) fd.append(k, v); });
  return api.post(`/loc/${id}/generate/${stage}`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const locMarkSent = (id, stage, fd) =>
  api.post(`/loc/${id}/mark-sent/${stage}`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
export const locProofBlob = (id, stage) =>
  api.get(`/loc/${id}/proof/${stage}`, { responseType: "blob" }).then((r) => r.data);
export const locResume = (id, body) => api.post(`/loc/${id}/resume`, body).then((r) => r.data);
export const locDeliver = (id, body) => api.post(`/loc/${id}/deliver`, body).then((r) => r.data);
export const locWhatsappSent = (id, body) => api.post(`/loc/${id}/whatsapp-sent`, body).then((r) => r.data);
export const locFollowup = (id, body) => api.post(`/loc/${id}/followup`, body).then((r) => r.data);

